/**
 * Model for the Filter & Sort panel.
 *
 * The panel edits an authored state (rows the user is assembling); pressing
 * Apply turns it into the `FilterSpec[]` / `SortSpec[]` the grid actually runs.
 * Keeping those separate is what makes Apply explicit rather than live.
 *
 * All SQL generation delegates to sqlgen's `filterExpression`, so there is
 * exactly one escaping implementation to audit. Nothing here builds SQL by
 * concatenating a raw user value.
 */

import { classifyType, isNumericClass, quoteIdent } from '@/lib/values';
import type { DuckDbType, TypeClass } from '@/types/api';
import type { FilterJoiner, FilterOperator, FilterSpec, SortDirection, SortSpec } from '@/cache/types';
import { filterExpression } from '@/cache/sqlgen';

export type PanelMode = 'builder' | 'text';

export interface FilterRow {
  readonly id: string;
  readonly enabled: boolean;
  readonly column: string;
  readonly operator: FilterOperator;
  readonly values: readonly string[];
  /** Joins this row to the next one. Ignored on the last row. */
  readonly joiner: FilterJoiner;
}

export interface SortRow {
  readonly id: string;
  readonly enabled: boolean;
  readonly column: string;
  readonly direction: SortDirection;
}

export interface FilterSortState {
  readonly mode: PanelMode;
  readonly rows: readonly FilterRow[];
  /** Raw predicate. Authoritative when mode is `text`. */
  readonly text: string;
  readonly sort: readonly SortRow[];
}

export const EMPTY_FILTER_SORT: FilterSortState = {
  mode: 'builder',
  rows: [],
  text: '',
  sort: [],
};

let sequence = 0;
export function rowId(prefix = 'f'): string {
  sequence += 1;
  return `${prefix}${sequence}_${Math.random().toString(36).slice(2, 7)}`;
}

/* ----------------------------------------------------------- operators --- */

/** How many value inputs an operator needs. */
export type Arity = 0 | 1 | 2 | 'list';

export interface OperatorMeta {
  readonly value: FilterOperator;
  readonly label: string;
  readonly arity: Arity;
  readonly classes: readonly TypeClass[];
}

const ALL: readonly TypeClass[] = [
  'number',
  'bigint',
  'boolean',
  'string',
  'temporal',
  'nested',
  'binary',
];
const ORDERED: readonly TypeClass[] = ['number', 'bigint', 'string', 'temporal'];
const TEXTUAL: readonly TypeClass[] = ['string', 'nested', 'binary'];

export const OPERATORS: readonly OperatorMeta[] = [
  { value: 'eq', label: '=', arity: 1, classes: ALL },
  { value: 'neq', label: '<>', arity: 1, classes: ALL },
  { value: 'gt', label: '>', arity: 1, classes: ORDERED },
  { value: 'gte', label: '>=', arity: 1, classes: ORDERED },
  { value: 'lt', label: '<', arity: 1, classes: ORDERED },
  { value: 'lte', label: '<=', arity: 1, classes: ORDERED },
  { value: 'like', label: 'like', arity: 1, classes: TEXTUAL },
  { value: 'notLike', label: 'not like', arity: 1, classes: TEXTUAL },
  { value: 'contains', label: 'contains', arity: 1, classes: TEXTUAL },
  { value: 'in', label: 'in', arity: 'list', classes: ALL },
  { value: 'notIn', label: 'not in', arity: 'list', classes: ALL },
  { value: 'between', label: 'between', arity: 2, classes: ORDERED },
  { value: 'isNull', label: 'is null', arity: 0, classes: ALL },
  { value: 'isNotNull', label: 'is not null', arity: 0, classes: ALL },
  { value: 'isEmpty', label: "is ''", arity: 0, classes: ['string'] },
];

const OPERATOR_BY_VALUE = new Map(OPERATORS.map((entry) => [entry.value, entry]));

export function operatorMeta(operator: FilterOperator): OperatorMeta {
  return OPERATOR_BY_VALUE.get(operator) ?? (OPERATORS[0] as OperatorMeta);
}

/** Operators offered for a column, gated by its type. */
export function operatorsFor(type: DuckDbType | undefined): readonly OperatorMeta[] {
  const cls = classifyType(type as DuckDbType);
  return OPERATORS.filter((entry) => entry.classes.includes(cls));
}

export function operatorAllowed(operator: FilterOperator, type: DuckDbType | undefined): boolean {
  return operatorsFor(type).some((entry) => entry.value === operator);
}

/** Falls back to a valid operator when the column type changes under a row. */
export function coerceOperator(
  operator: FilterOperator,
  type: DuckDbType | undefined,
): FilterOperator {
  if (operatorAllowed(operator, type)) return operator;
  return operatorsFor(type)[0]?.value ?? 'eq';
}

export function defaultOperatorFor(type: DuckDbType | undefined): FilterOperator {
  return isNumericClass(classifyType(type as DuckDbType)) ? 'eq' : 'contains';
}

export function arityOf(operator: FilterOperator): Arity {
  return operatorMeta(operator).arity;
}

/** True when the row's value editor should be hidden entirely. */
export function takesNoValue(operator: FilterOperator): boolean {
  return arityOf(operator) === 0;
}

/* ------------------------------------------------------------- rows ------ */

export function newFilterRow(column: string, type: DuckDbType | undefined): FilterRow {
  return {
    id: rowId('f'),
    enabled: true,
    column,
    operator: defaultOperatorFor(type),
    values: [],
    joiner: 'and',
  };
}

export function newSortRow(column: string): SortRow {
  return { id: rowId('s'), enabled: true, column, direction: 'asc' };
}

/** A row contributes to the query only when enabled, bound and complete. */
export function isRowApplicable(row: FilterRow): boolean {
  if (!row.enabled || row.column === '') return false;
  const arity = arityOf(row.operator);
  if (arity === 0) return true;
  if (arity === 'list') return row.values.some((value) => value !== '');
  if (arity === 2) return row.values.length >= 2 && row.values.every((value) => value !== '');
  return (row.values[0] ?? '') !== '';
}

export function toFilterSpecs(rows: readonly FilterRow[]): FilterSpec[] {
  return rows.filter(isRowApplicable).map((row) => ({
    column: row.column,
    operator: row.operator,
    values:
      arityOf(row.operator) === 'list'
        ? row.values.filter((value) => value !== '')
        : row.values,
    joiner: row.joiner,
  }));
}

export function toSortSpecs(rows: readonly SortRow[]): SortSpec[] {
  // List order is the sort precedence, and it must be preserved exactly.
  return rows
    .filter((row) => row.enabled && row.column !== '')
    .map((row) => ({ column: row.column, direction: row.direction }));
}

export function moveSortRow(rows: readonly SortRow[], id: string, delta: -1 | 1): SortRow[] {
  const next = [...rows];
  const index = next.findIndex((row) => row.id === id);
  const target = index + delta;
  if (index === -1 || target < 0 || target >= next.length) return next;
  const moved = next[index];
  const displaced = next[target];
  if (!moved || !displaced) return next;
  next[index] = displaced;
  next[target] = moved;
  return next;
}

/* -------------------------------------------------------- text mode ------ */

/**
 * Renders the builder rows as a SQL predicate.
 *
 * Precedence is plain SQL: AND binds tighter than OR. Each predicate is
 * parenthesised individually so an operator like BETWEEN cannot rebind, but the
 * joiners are left unparenthesised so what the user sees in Text mode is what
 * the builder means.
 */
export function serializePredicate(
  rows: readonly FilterRow[],
  types: ReadonlyMap<string, DuckDbType>,
): string {
  const applicable = rows.filter(isRowApplicable);
  if (applicable.length === 0) return '';

  return applicable
    .map((row, index) => {
      const sql = filterExpression(
        {
          column: row.column,
          operator: row.operator,
          values:
            arityOf(row.operator) === 'list'
              ? row.values.filter((value) => value !== '')
              : row.values,
        },
        types.get(row.column) ?? 'VARCHAR',
      );
      const joiner =
        index < applicable.length - 1 ? ` ${(row.joiner ?? 'and').toUpperCase()} ` : '';
      return `${sql}${joiner}`;
    })
    .join('');
}

export interface ParseResult {
  readonly rows: readonly FilterRow[] | null;
  /** Set when the text cannot be represented in the builder. */
  readonly reason: string | null;
}

const TOKEN =
  /^\s*(?:"((?:[^"]|"")+)"|([A-Za-z_][\w$]*))\s+(IS\s+NOT\s+NULL|IS\s+NULL|NOT\s+ILIKE|NOT\s+LIKE|ILIKE|LIKE|NOT\s+IN|IN|BETWEEN|<>|!=|>=|<=|=|>|<)\s*/i;

function unquoteLiteral(raw: string): string {
  return raw.slice(1, -1).replace(/''/g, "'");
}

/** Reads one SQL literal (quoted string or bare number) from the head of `text`. */
function readLiteral(text: string): { value: string; rest: string } | null {
  const trimmed = text.replace(/^\s+/, '');
  if (trimmed.startsWith("'")) {
    let index = 1;
    while (index < trimmed.length) {
      if (trimmed[index] === "'") {
        if (trimmed[index + 1] === "'") {
          index += 2;
          continue;
        }
        return {
          value: unquoteLiteral(trimmed.slice(0, index + 1)),
          rest: trimmed.slice(index + 1),
        };
      }
      index += 1;
    }
    return null;
  }
  const number = /^-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?/.exec(trimmed);
  if (number?.[0]) return { value: number[0], rest: trimmed.slice(number[0].length) };
  return null;
}

const OPERATOR_TEXT: Record<string, FilterOperator> = {
  '=': 'eq',
  '<>': 'neq',
  '!=': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  LIKE: 'like',
  ILIKE: 'like',
  'NOT LIKE': 'notLike',
  'NOT ILIKE': 'notLike',
  IN: 'in',
  'NOT IN': 'notIn',
  BETWEEN: 'between',
  'IS NULL': 'isNull',
  'IS NOT NULL': 'isNotNull',
};

/**
 * Parses a predicate back into builder rows.
 *
 * Only the shapes the builder itself emits are supported. Anything else -- a
 * function call, nested parentheses, a subquery -- returns a reason instead,
 * and the caller keeps Text as the source of truth. A hand-written predicate is
 * never silently discarded.
 */
export function parsePredicate(text: string): ParseResult {
  const trimmed = text.trim();
  if (trimmed === '') return { rows: [], reason: null };

  const rows: FilterRow[] = [];
  let rest = trimmed;
  let guard = 0;

  while (rest.trim() !== '' && guard < 200) {
    guard += 1;

    // Our own serializer wraps a few operators in parentheses.
    let hadParen = false;
    const openParen = /^\s*\(/.exec(rest);
    if (openParen) {
      hadParen = true;
      rest = rest.replace(/^\s*\(/, '');
    }

    const head = TOKEN.exec(rest);
    if (!head) {
      return { rows: null, reason: 'The expression uses syntax the builder cannot represent.' };
    }

    const column = head[1] ? head[1].replace(/""/g, '"') : (head[2] ?? '');
    const rawOperator = (head[3] ?? '').toUpperCase().replace(/\s+/g, ' ');
    const operator = OPERATOR_TEXT[rawOperator];
    if (!operator) {
      return { rows: null, reason: `Unsupported operator \`${rawOperator}\`.` };
    }

    rest = rest.slice(head[0].length);
    const values: string[] = [];

    if (operator === 'between') {
      const lo = readLiteral(rest);
      if (!lo) return { rows: null, reason: 'BETWEEN needs two literal bounds.' };
      rest = lo.rest;
      const and = /^\s*AND\s+/i.exec(rest);
      if (!and) return { rows: null, reason: 'BETWEEN needs an AND between its bounds.' };
      rest = rest.slice(and[0].length);
      const hi = readLiteral(rest);
      if (!hi) return { rows: null, reason: 'BETWEEN needs two literal bounds.' };
      rest = hi.rest;
      values.push(lo.value, hi.value);
    } else if (operator === 'in' || operator === 'notIn') {
      const open = /^\s*\(/.exec(rest);
      if (!open) return { rows: null, reason: 'IN needs a parenthesised list.' };
      rest = rest.slice(open[0].length);
      for (;;) {
        const literal = readLiteral(rest);
        if (!literal) return { rows: null, reason: 'IN list must contain literals only.' };
        values.push(literal.value);
        rest = literal.rest;
        const comma = /^\s*,/.exec(rest);
        if (comma) {
          rest = rest.slice(comma[0].length);
          continue;
        }
        const close = /^\s*\)/.exec(rest);
        if (!close) return { rows: null, reason: 'Unclosed IN list.' };
        rest = rest.slice(close[0].length);
        break;
      }
    } else if (operator !== 'isNull' && operator !== 'isNotNull') {
      const literal = readLiteral(rest);
      if (!literal) {
        return { rows: null, reason: 'Only literal comparison values are supported.' };
      }
      values.push(literal.value);
      rest = literal.rest;
    }

    if (hadParen) {
      const close = /^\s*\)/.exec(rest);
      if (!close) return { rows: null, reason: 'Unbalanced parentheses.' };
      rest = rest.slice(close[0].length);
    }

    const joinerMatch = /^\s*(AND|OR)\s+/i.exec(rest);
    const joiner: FilterJoiner =
      joinerMatch?.[1]?.toUpperCase() === 'OR' ? 'or' : 'and';
    if (joinerMatch) rest = rest.slice(joinerMatch[0].length);

    rows.push({ id: rowId('f'), enabled: true, column, operator, values, joiner });

    if (!joinerMatch && rest.trim() !== '') {
      return { rows: null, reason: 'The expression uses syntax the builder cannot represent.' };
    }
  }

  return { rows, reason: null };
}

/* ------------------------------------------------------------- helpers --- */

/** `SELECT DISTINCT` for the Suggested Values list. Runs against the cache. */
export function distinctValuesSql(table: string, column: string, limit = 500): string {
  const col = quoteIdent(column);
  return [
    `SELECT ${col} AS v, ${col} IS NULL AS is_null, count(*) AS n`,
    `FROM ${quoteIdent(table)}`,
    'GROUP BY 1, 2 ORDER BY n DESC, 1 ASC',
    `LIMIT ${Math.max(1, Math.trunc(limit))}`,
  ].join('\n');
}

export function describeFilterSort(state: FilterSortState): string {
  const filters =
    state.mode === 'text'
      ? state.text.trim() === ''
        ? 0
        : 1
      : state.rows.filter(isRowApplicable).length;
  const sorts = state.sort.filter((row) => row.enabled && row.column !== '').length;

  if (filters === 0 && sorts === 0) return '';
  const parts: string[] = [];
  if (filters > 0) parts.push(`${filters} filter${filters === 1 ? '' : 's'}`);
  if (sorts > 0) parts.push(`${sorts} sort${sorts === 1 ? '' : 's'}`);
  return parts.join(' · ');
}
